import { observer } from 'mobx-react-lite';
import { Animated, StyleSheet, Text, View } from 'react-native';
import { FocusStateType } from '../../../../common/hooks';
import { computed } from 'mobx';
import AppColors from '../../../../common/colors';
import useGlobalStyles from '../../../../common/style_context';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import useScale from '../../../../common/hooks/useScale';
import TvPressable from '../../../../components/tv_pressable';
import { VoidCb } from '../../../../types/others';

type Props = {
  text: string;
  value?: string | number | boolean;
  fraction?: number;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const itemHeight = 60;
const itemGap = 15;

const styles = [
  `fr,ac,h=${itemHeight},mb=${itemGap},ph=24`,
  `fill,bg=${AppColors.mainBlue},br=12`,
  `hide,fill,bg=${AppColors.mainBlue},br=12`,
  'f=1,fs=28,fw=700,c=white',
  'fs=28,fw=400,c=white',
];

const OptionsButton = observer<Props>(
  ({ text, value, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [
      container,
      focusedContainer,
      unfocusedContainer,
      textStyle,
      valueStyle,
    ] = useGlobalStyles(styles);

    const valueComponent = useMemo(() => {
      if (value === undefined) return null;
      const t = typeof value;
      if (t === 'string') return <Text style={valueStyle}>{value}</Text>;
      else if (t === 'number') return <Text style={valueStyle}>{value}</Text>;
      return <Switch value={value === true} focused={focused} />;
    }, [value, valueStyle, focused]);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <Text style={textStyle}>{text}</Text>
        {valueComponent}
      </TvPressable>
    );
  },
);

type SwitchProps = {
  value: boolean;
  focused?: boolean;
};

const switchStyles = [
  'jc,w=36,h=20',
  'w=36,h=12,br=150',
  'abs,w=20,h=20,br=150',
];

const Switch = memo<SwitchProps>(({ value, focused }) => {
  const scales = useScale();
  const anim = useRef(new Animated.Value(value ? 1 : 0)).current;

  const [container, backStyle, handleStyle] = useGlobalStyles(switchStyles);

  useEffect(() => {
    Animated.timing(anim, {
      duration: 300,
      toValue: value ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [value]);

  const back = useMemo(
    () =>
      //@ts-ignore interpolating color
      StyleSheet.compose(backStyle, {
        backgroundColor: anim.interpolate({
          inputRange: [0, 1],
          outputRange: ['#303437', '#81C7FE'],
        }),
      }),
    [backStyle],
  );
  const handle = useMemo(() => {
    const inputRange = [0, 1];
    //@ts-ignore interpolating color
    return StyleSheet.compose(handleStyle, {
      backgroundColor: anim.interpolate({
        inputRange: [0, 1],
        outputRange: ['white', focused ? 'white' : AppColors.mainBlue],
      }),
      left: anim.interpolate({
        inputRange,
        outputRange: [0, (16 / scales[0]) * scales[1]],
      }),
    });
  }, [handleStyle, scales, focused]);

  return (
    <View style={container}>
      <Animated.View style={back} />
      <Animated.View style={handle} />
    </View>
  );
});

export default OptionsButton;
